import { formatRelative } from 'date-fns';
import { ar } from 'date-fns/locale';
import { CalendarRange, FilePenLine, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { toast } from 'sonner';

import type { Endpoints } from '@cold-ambulance/client';
import {
  Badge,
  Card,
  CardContent,
  buttonVariants,
  cn,
} from '@cold-ambulance/shadcn';
import { ConfirmDialog, formatDate, useAction } from '@cold-ambulance/ui';

import { clearLocalDraft } from '../hooks/use-request-draft.ts';

type RequestDraft = Endpoints['GET /me/request-drafts']['output'][number];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getDraftProjectName(data: RequestDraft['data'], fallback: string) {
  if (!isRecord(data) || typeof data.projectName !== 'string') {
    return fallback;
  }

  return data.projectName.trim() || fallback;
}

function getDraftLocationName(data: RequestDraft['data']) {
  if (!isRecord(data) || !isRecord(data.location)) {
    return null;
  }

  return typeof data.location.name === 'string' ? data.location.name : null;
}

function getDraftDates(data: RequestDraft['data']) {
  if (!isRecord(data) || !isRecord(data.dates)) {
    return null;
  }

  const from = typeof data.dates.from === 'string' ? data.dates.from : null;
  const to = typeof data.dates.to === 'string' ? data.dates.to : null;

  if (!from || !to) {
    return null;
  }

  return { from, to };
}

export function RequestDraftCard({ draft }: { draft: RequestDraft }) {
  const { i18n, t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const deleteDraft = useAction('DELETE /me/request-drafts/{id}', {
    invalidate: ['GET /me/request-drafts'],
    meta: { toast: true },
    mutationFn: (dispatch) => dispatch({ id: draft.id }),
    onSuccess: () => {
      clearLocalDraft(draft.id);
      setConfirmOpen(false);
      toast.success(t('shared.actions.draft.deleted'));
    },
  });
  const locationName = getDraftLocationName(draft.data);
  const dates = getDraftDates(draft.data);

  return (
    <Card className="border-primary/40 bg-white py-6">
      <CardContent className="flex h-full flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-2">
              <Badge variant="secondary">{t('requests.draftBadge')}</Badge>
            </div>
            <h2 className="truncate text-lg font-bold">
              {getDraftProjectName(draft.data, t('home.defaultProjectName'))}
            </h2>
            <p className="text-muted-foreground text-sm">
              {t('requests.lastSaved', {
                value: formatRelative(new Date(draft.updatedAt), new Date(), {
                  locale: i18n.language.startsWith('ar') ? ar : undefined,
                }),
              })}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={deleteDraft.isPending}
            aria-label={t('requests.deleteDraft')}
            title={t('requests.deleteDraft')}
            className={cn(
              buttonVariants({ variant: 'ghost', size: 'icon' }),
              'text-destructive hover:text-destructive/80',
            )}
          >
            <Trash2 className="size-4" />
          </button>
        </div>

        {locationName ? (
          <p className="text-muted-foreground text-sm">{locationName}</p>
        ) : null}

        {dates ? (
          <p className="text-muted-foreground flex items-center gap-2 text-sm">
            <CalendarRange className="size-4" />
            <span>
              {formatDate(dates.from)} - {formatDate(dates.to)}
            </span>
          </p>
        ) : null}

        <Link
          to={`/requests/drafts/${draft.id}`}
          className={buttonVariants({
            variant: 'outline',
            className:
              'border-primary text-primary mt-auto !h-11 w-full bg-transparent px-6 font-bold',
          })}
        >
          <FilePenLine className="size-4" />
          {t('requests.continueEditing')}
        </Link>
      </CardContent>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        mutation={deleteDraft}
        title={t('requests.deleteDraft')}
        description={t('requests.deleteDraftConfirmation')}
      />
    </Card>
  );
}
